import {
  collection,
  doc,
  getDocs,
  query,
  type Query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { sanitizeAgoraChannelName } from './sanitizeAgoraChannelName';

const VIDEO_SESSIONS = 'video_call_sessions';

/** Fields cleared when the meet app disconnects (aligns with main-site idle state). */
const USER_DISCONNECT_CALL_CLEAR = {
  call_status: '',
  video_Url: '',
  accept_Url: '',
  call_Started_By: '',
  call_started_by_docId: '',
} as const;

export type ClearCurrentUserCallStateOptions = {
  /** Agora / token numeric uid — matches Firestore `userId` and often the `users/{docId}` id. */
  agoraUid?: number | null;
  /** Channel name from the room (helps URL-candidate matching when query params differ). */
  channelName?: string | null;
};

/**
 * Main-site meet links often include `username=User-432` (see getUserName). That id matches `users/{docId}`.
 * Also supports optional `firestore_user_id`, `hui_user_id`, `userDocId`, `docId` query params if present.
 */
export function parseFirestoreUserDocIdFromMeetUrl(href: string): string | null {
  if (!href || typeof href !== 'string') return null;
  try {
    const u = new URL(href);
    const sp = u.searchParams;
    for (const key of ['firestore_user_id', 'hui_user_id', 'userDocId', 'docId']) {
      const v = sp.get(key);
      if (v && /^\d+$/.test(v.trim())) {
        return v.trim();
      }
    }
    const raw = sp.get('username');
    if (!raw) return null;
    const username = decodeURIComponent(String(raw).replace(/\+/g, ' ')).trim();
    const spaced = username.match(/^User\s*[-–+]\s*(\d+)\s*$/i);
    if (spaced) return spaced[1];
    const userNum = username.match(/^User\s+(\d+)\s*$/i);
    if (userNum) return userNum[1];
    const compact = username.match(/^User(\d+)$/i);
    if (compact) return compact[1];
    return null;
  } catch {
    return null;
  }
}

function collectFirestoreUserDocIdsFromMeetApp(): string[] {
  if (typeof window === 'undefined') return [];
  const ids = new Set<string>();
  const add = (href: string) => {
    const id = parseFirestoreUserDocIdFromMeetUrl(href);
    if (id) ids.add(id);
  };
  add(window.location.href);
  try {
    const remembered = sessionStorage.getItem('hui_video_call_page_href');
    if (remembered) add(remembered);
  } catch {
    /* ignore */
  }
  return [...ids];
}

export function channelSessionDocId(channelName: string): string {
  const safe = sanitizeAgoraChannelName(channelName);
  return encodeURIComponent(safe).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 120);
}

export function getProfileRedirectUrl(): string {
  const fromEnv = process.env.REACT_APP_PROFILE_REDIRECT_URL;
  if (fromEnv && fromEnv.trim()) {
    return fromEnv.trim();
  }
  return 'https://beta.hui.land/profile';
}

/**
 * Build every plausible URL that the main site might have stored as `video_Url` in Firestore.
 * We try all combinations of: with/without each optional query param, with/without trailing slash,
 * encoded/decoded, and alternate origins.
 */
export function getVideoCallUrlCandidates(href: string): string[] {
  const out = new Set<string>();

  try {
    const remembered = sessionStorage.getItem('hui_video_call_page_href');
    if (remembered) {
      out.add(remembered);
    }
  } catch {
    /* ignore */
  }

  const add = (s: string) => {
    if (!s) return;
    out.add(s);
    try {
      const decoded = decodeURIComponent(s);
      if (decoded !== s) out.add(decoded);
    } catch {
      /* ignore */
    }
  };

  try {
    const u = new URL(href);
    u.hash = '';
    add(u.toString());
    const params = new URLSearchParams(u.search);

    const origins = new Set<string>([u.origin]);
    const alt = process.env.REACT_APP_FIRESTORE_VIDEO_URL_ALTERNATE_ORIGINS;
    if (alt) {
      alt.split(',').map(s => s.trim()).filter(Boolean).forEach(o => origins.add(o));
    }

    const paths = new Set<string>();
    const cleanPath = u.pathname.replace(/\/$/, '') || '';
    paths.add(cleanPath);
    paths.add(cleanPath + '/');

    // Build all query-string combinations the main site might have stored:
    // 1. Full current params
    // 2. Only passcode
    // 3. passcode + room
    // The main site typically stores: origin?passcode=XXX  or  origin?passcode=XXX&room=YYY
    const paramCombinations: string[] = [];
    const passcode = params.get('passcode');

    if (u.search) {
      paramCombinations.push(u.search);
    }

    if (passcode) {
      const pcOnly = new URLSearchParams();
      pcOnly.set('passcode', passcode);
      paramCombinations.push('?' + pcOnly.toString());

      const room = params.get('room');
      if (room) {
        const pcRoom = new URLSearchParams();
        pcRoom.set('passcode', passcode);
        pcRoom.set('room', room);
        paramCombinations.push('?' + pcRoom.toString());
      }
    }

    if (paramCombinations.length === 0) {
      paramCombinations.push('');
    }

    for (const origin of origins) {
      for (const path of paths) {
        for (const qs of paramCombinations) {
          add(`${origin}${path}${qs}`);
        }
      }
    }
  } catch {
    /* ignore */
  }

  add(href);
  return [...out];
}

/**
 * Clears `call_status`, `video_Url`, `call_Started_By` on every `users` doc whose `video_Url`
 * matches any candidate URL for the current page.
 */
export async function clearFirestoreUsersForCurrentPage(): Promise<number> {
  const candidates = getVideoCallUrlCandidates(window.location.href);
  console.log('[videoCallFirestore] URL candidates for Firestore query:', candidates);

  const seen = new Set<string>();
  const snaps = await Promise.all(
    candidates.map(url => getDocs(query(collection(db, 'users'), where('video_Url', '==', url))))
  );

  const updates: Promise<void>[] = [];
  let total = 0;
  for (const snap of snaps) {
    for (const userDoc of snap.docs) {
      if (seen.has(userDoc.id)) continue;
      seen.add(userDoc.id);
      total += 1;
      console.log(`[videoCallFirestore] clearing user doc: ${userDoc.id}`);
      updates.push(
        updateDoc(doc(db, 'users', userDoc.id), {
          ...USER_DISCONNECT_CALL_CLEAR,
        }).catch(err => {
          console.error(`[videoCallFirestore] failed to clear user ${userDoc.id}:`, err);
        })
      );
    }
  }
  await Promise.all(updates);
  console.log(`[videoCallFirestore] total docs cleared: ${total}`);
  return total;
}

/**
 * Clears call fields for the current participant only (not every user on the URL).
 * Use when a participant leaves without ending the room for everyone.
 */
export async function clearCurrentUserCallState(
  localIdentity?: string,
  options?: ClearCurrentUserCallStateOptions
): Promise<number> {
  const uid = auth.currentUser?.uid ?? '';
  const email = auth.currentUser?.email ?? '';
  const identity = (localIdentity ?? '').trim().toLowerCase();
  const agoraUid = options?.agoraUid;
  const channelName = (options?.channelName ?? '').trim();
  const meetUrlFirestoreDocIds = collectFirestoreUserDocIdsFromMeetApp();
  const seen = new Set<string>();
  const updates: Promise<void>[] = [];

  const queueClear = (id: string) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    updates.push(
      updateDoc(doc(db, 'users', id), {
        ...USER_DISCONNECT_CALL_CLEAR,
      }).catch(err => {
        console.error(`[videoCallFirestore] failed to clear current user ${id}:`, err);
      })
    );
  };

  // Primary path: users doc id equals Firebase auth uid.
  if (uid) {
    queueClear(uid);
  }

  // Meet URL carries business user id (e.g. callee opens accept link with username=User-432).
  for (const businessId of meetUrlFirestoreDocIds) {
    queueClear(businessId);
  }

  // Meet app: Firestore `users` doc id is usually the business user id, same as Agora uid from the token.
  if (agoraUid != null && Number.isFinite(agoraUid) && agoraUid > 0) {
    queueClear(String(Math.trunc(agoraUid)));
  }

  // Fallback path: locate the current user's row among users mapped to this call URL.
  const hrefForCandidates =
    channelName && window.location.href
      ? (() => {
          try {
            const u = new URL(window.location.href);
            const pc = u.searchParams.get('passcode');
            if (pc) {
              const alt = new URL(u.origin + u.pathname);
              alt.searchParams.set('passcode', pc);
              alt.searchParams.set('room', channelName);
              return alt.toString();
            }
          } catch {
            /* ignore */
          }
          return window.location.href;
        })()
      : window.location.href;

  const candidateUrlSet = new Set<string>([
    ...getVideoCallUrlCandidates(window.location.href),
    ...getVideoCallUrlCandidates(hrefForCandidates),
  ]);
  const candidates = [...candidateUrlSet];
  const snapsVideo = await Promise.all(
    candidates.map(url => getDocs(query(collection(db, 'users'), where('video_Url', '==', url))))
  );
  const snapsAccept = await Promise.all(
    candidates.map(url => getDocs(query(collection(db, 'users'), where('accept_Url', '==', url))))
  );
  const candidateDocById = new Map<string, (typeof snapsVideo)[0]['docs'][0]>();
  for (const s of [...snapsVideo, ...snapsAccept]) {
    for (const d of s.docs) {
      candidateDocById.set(d.id, d);
    }
  }
  const candidateDocs = [...candidateDocById.values()];

  const norm = (v: unknown) => String(v ?? '').trim().toLowerCase();
  const uidSet = new Set([uid, norm(uid)].filter(Boolean));
  if (agoraUid != null && Number.isFinite(agoraUid) && agoraUid > 0) {
    const n = Math.trunc(agoraUid);
    uidSet.add(String(n));
    uidSet.add(norm(n));
    uidSet.add(norm(String(n)));
  }
  for (const businessId of meetUrlFirestoreDocIds) {
    uidSet.add(businessId);
    uidSet.add(norm(businessId));
  }
  const emailSet = new Set([email, norm(email)].filter(Boolean));
  const identitySet = new Set([identity].filter(Boolean));

  candidateDocs.forEach(userDoc => {
    const data = userDoc.data() as Record<string, unknown>;
    const values = [
      userDoc.id,
      data.uid,
      data.user_uid,
      data.userId,
      data.firebase_uid,
      data.auth_uid,
      data.email,
      data.user_email,
      data.identity,
      data.displayName,
      data.name,
    ].map(norm);
    const matchesUid = values.some(v => uidSet.has(v));
    const matchesEmail = values.some(v => emailSet.has(v));
    const matchesIdentity = values.some(v => identitySet.has(v));
    if (matchesUid || matchesEmail || matchesIdentity) {
      queueClear(userDoc.id);
    }
  });

  // Direct lookup fallbacks (outside video_Url matching) for deployments where URL values differ.
  const agoraN = agoraUid != null && Number.isFinite(agoraUid) && agoraUid > 0 ? Math.trunc(agoraUid) : null;
  const directQueries = [
    uid ? query(collection(db, 'users'), where('uid', '==', uid)) : null,
    uid ? query(collection(db, 'users'), where('user_uid', '==', uid)) : null,
    uid ? query(collection(db, 'users'), where('userId', '==', uid)) : null,
    uid ? query(collection(db, 'users'), where('firebase_uid', '==', uid)) : null,
    uid ? query(collection(db, 'users'), where('auth_uid', '==', uid)) : null,
    email ? query(collection(db, 'users'), where('email', '==', email)) : null,
    email ? query(collection(db, 'users'), where('user_email', '==', email)) : null,
    localIdentity ? query(collection(db, 'users'), where('identity', '==', localIdentity)) : null,
    localIdentity ? query(collection(db, 'users'), where('displayName', '==', localIdentity)) : null,
    localIdentity ? query(collection(db, 'users'), where('name', '==', localIdentity)) : null,
    agoraN != null ? query(collection(db, 'users'), where('userId', '==', agoraN)) : null,
    agoraN != null ? query(collection(db, 'users'), where('userId', '==', String(agoraN))) : null,
  ].filter((q): q is Query => q != null);
  const directSnaps = await Promise.all(directQueries.map(q => getDocs(q)));
  directSnaps.forEach(snap => snap.docs.forEach(d => queueClear(d.id)));

  // Last fallback: if we still didn't match anything but only one candidate row exists, clear it.
  if (seen.size === 0 && candidateDocs.length === 1) {
    queueClear(candidateDocs[0].id);
  }

  await Promise.all(updates);
  return seen.size;
}

export async function markChannelSessionJoined(channelName: string): Promise<void> {
  const id = channelSessionDocId(channelName);
  const ref = doc(db, VIDEO_SESSIONS, id);
  await setDoc(
    ref,
    {
      channelName: sanitizeAgoraChannelName(channelName),
      ended: false,
      endedAt: null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function signalChannelSessionEnded(channelName: string): Promise<void> {
  const id = channelSessionDocId(channelName);
  const ref = doc(db, VIDEO_SESSIONS, id);
  await setDoc(
    ref,
    {
      channelName: sanitizeAgoraChannelName(channelName),
      ended: true,
      endedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}
